/**
 * CL-339B: Canonical workflow role capabilities (encoded from PROJECT_STATUS.md CL-339A).
 * Pure helpers only — persona resolution order and profile/auth context stay in displayUtils.
 */

function safeStr(value) {
  return (value ?? "").toString().trim().toLowerCase();
}

/** Known roster / persona keys (normalized lowercase). */
export const WORKFLOW_ROLE_KEYS = Object.freeze([
  "client",
  "designer",
  "engineer",
  "supplier",
  "contractor",
  "consultant",
  "reviewer",
  "trade",
  "subtrade",
  "collaborator",
  "project_admin",
  "internal",
  "guest",
  "admin",
]);

/** Persona pick order when multiple roles exist on a profile (CL-338F / CL-339A). */
export const WORKFLOW_ROLE_PERSONA_PRIORITY = Object.freeze([
  "project_admin",
  "client",
  "designer",
  "engineer",
  "consultant",
  "reviewer",
  "supplier",
  "contractor",
  "trade",
  "subtrade",
  "collaborator",
]);

/** Roster roles that use contributor workflow UX (designer-style). */
export const CONTRIBUTOR_WORKFLOW_ROLES = Object.freeze([
  "designer",
  "engineer",
  "supplier",
  "contractor",
  "consultant",
  "reviewer",
  "trade",
  "subtrade",
  "collaborator",
]);

/**
 * @typedef {"client"|"contributor"|"admin"|"guest"} WorkflowAudience
 */

/**
 * @typedef {object} WorkflowRoleConfig
 * @property {string} role
 * @property {string} portalLabel
 * @property {string} headingLabel
 * @property {WorkflowAudience} workflowAudience
 * @property {string} typicalAuthRole
 * @property {boolean} canApprove
 * @property {boolean} canRequestChanges
 * @property {boolean} canUploadRevision
 * @property {boolean} canComment
 * @property {boolean} canAskQuestion
 * @property {boolean} canManageCollaborators
 * @property {boolean} canAccessAdminWorkspace
 * @property {boolean} usesContributorProjectListCopy
 * @property {boolean} isContributor
 * @property {boolean} isClient
 * @property {boolean} isProjectAdmin
 */

const CONTRIBUTOR_CAPABILITIES = Object.freeze({
  canApprove: false,
  canRequestChanges: false,
  canUploadRevision: true,
  canComment: true,
  canAskQuestion: true,
  canManageCollaborators: false,
  canAccessAdminWorkspace: false,
  usesContributorProjectListCopy: true,
  isContributor: true,
  isClient: false,
  isProjectAdmin: false,
});

/** @type {Record<string, WorkflowRoleConfig>} */
const WORKFLOW_ROLE_MATRIX = Object.freeze({
  client: Object.freeze({
    role: "client",
    portalLabel: "CLIENT PORTAL",
    headingLabel: "Client",
    workflowAudience: "client",
    typicalAuthRole: "client",
    canApprove: true,
    canRequestChanges: true,
    canUploadRevision: false,
    canComment: true,
    canAskQuestion: false,
    canManageCollaborators: false,
    canAccessAdminWorkspace: false,
    usesContributorProjectListCopy: false,
    isContributor: false,
    isClient: true,
    isProjectAdmin: false,
  }),
  designer: Object.freeze({
    role: "designer",
    portalLabel: "DESIGNER PORTAL",
    headingLabel: "Designer",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  engineer: Object.freeze({
    role: "engineer",
    portalLabel: "ENGINEER PORTAL",
    headingLabel: "Engineer",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  supplier: Object.freeze({
    role: "supplier",
    portalLabel: "SUPPLIER PORTAL",
    headingLabel: "Supplier",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  contractor: Object.freeze({
    role: "contractor",
    portalLabel: "CONTRACTOR PORTAL",
    headingLabel: "Contractor",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  consultant: Object.freeze({
    role: "consultant",
    portalLabel: "CONSULTANT PORTAL",
    headingLabel: "Consultant",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  reviewer: Object.freeze({
    role: "reviewer",
    portalLabel: "REVIEWER PORTAL",
    headingLabel: "Reviewer",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  trade: Object.freeze({
    role: "trade",
    portalLabel: "TRADE PORTAL",
    headingLabel: "Trade",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  subtrade: Object.freeze({
    role: "subtrade",
    portalLabel: "TRADE PORTAL",
    headingLabel: "Trade",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  collaborator: Object.freeze({
    role: "collaborator",
    portalLabel: "COLLABORATOR PORTAL",
    headingLabel: "Collaborator",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  project_admin: Object.freeze({
    role: "project_admin",
    portalLabel: "PROJECT ADMINISTRATION",
    headingLabel: "Project administration",
    workflowAudience: "admin",
    typicalAuthRole: "admin",
    canApprove: true,
    canRequestChanges: true,
    canUploadRevision: true,
    canComment: true,
    canAskQuestion: true,
    canManageCollaborators: true,
    canAccessAdminWorkspace: true,
    usesContributorProjectListCopy: false,
    isContributor: false,
    isClient: false,
    isProjectAdmin: true,
  }),
  admin: Object.freeze({
    role: "admin",
    portalLabel: "ADMIN PORTAL",
    headingLabel: "Admin",
    workflowAudience: "admin",
    typicalAuthRole: "admin",
    canApprove: true,
    canRequestChanges: true,
    canUploadRevision: true,
    canComment: true,
    canAskQuestion: true,
    canManageCollaborators: true,
    canAccessAdminWorkspace: true,
    usesContributorProjectListCopy: false,
    isContributor: false,
    isClient: false,
    isProjectAdmin: false,
  }),
  internal: Object.freeze({
    role: "internal",
    portalLabel: "CLIENT PORTAL",
    headingLabel: "Client",
    workflowAudience: "contributor",
    typicalAuthRole: "construction_collaborator",
    ...CONTRIBUTOR_CAPABILITIES,
  }),
  guest: Object.freeze({
    role: "guest",
    portalLabel: "GUEST PORTAL",
    headingLabel: "Guest",
    workflowAudience: "contributor",
    typicalAuthRole: "",
    canApprove: false,
    canRequestChanges: false,
    canUploadRevision: false,
    canComment: false,
    canAskQuestion: false,
    canManageCollaborators: false,
    canAccessAdminWorkspace: false,
    usesContributorProjectListCopy: false,
    isContributor: false,
    isClient: false,
    isProjectAdmin: false,
  }),
});

const DEFAULT_ROLE_CONFIG = WORKFLOW_ROLE_MATRIX.client;

/** @param {string} [role] */
export function normalizeWorkflowRole(role) {
  return safeStr(role);
}

/**
 * @param {string} [role]
 * @returns {WorkflowRoleConfig}
 */
export function getWorkflowRoleConfig(role) {
  const key = normalizeWorkflowRole(role);
  if (!key) return DEFAULT_ROLE_CONFIG;
  return WORKFLOW_ROLE_MATRIX[key] || WORKFLOW_ROLE_MATRIX.internal;
}

/** @param {string} [role] */
export function getPortalPersonaLabel(role) {
  const key = normalizeWorkflowRole(role);
  if (WORKFLOW_ROLE_MATRIX[key]) return WORKFLOW_ROLE_MATRIX[key].portalLabel;
  if (isContributorPersonaRole(key) || getWorkflowRoleConfig(role).workflowAudience === "contributor") {
    return "PROJECT PORTAL";
  }
  return getWorkflowRoleConfig(role).portalLabel;
}

/** Sentence-case label for headings (e.g. "Designer"). */
export function getPortalPersonaHeadingLabel(role) {
  return getWorkflowRoleConfig(role).headingLabel;
}

/** @param {string} [role] @returns {WorkflowAudience} */
export function getWorkflowAudience(role) {
  return getWorkflowRoleConfig(role).workflowAudience;
}

/** @param {string} [role] */
export function canApprove(role) {
  return getWorkflowRoleConfig(role).canApprove;
}

/** @param {string} [role] */
export function canRequestChanges(role) {
  return getWorkflowRoleConfig(role).canRequestChanges;
}

/**
 * Base capability from roster role; file/share context still required in UI.
 * @param {string} [role]
 * @param {{ isAnonymous?: boolean, isRevisionsRequired?: boolean, shareAllowsUpload?: boolean, requiresSharePermission?: boolean }} [context]
 */
export function canUploadRevision(role, context = {}) {
  const cfg = getWorkflowRoleConfig(role);
  if (!cfg.canUploadRevision) return false;
  if (context.isAnonymous) return false;
  if (context.isRevisionsRequired === false) return false;
  if (context.requiresSharePermission && context.shareAllowsUpload === false) return false;
  return true;
}

/**
 * @param {string} [role]
 * @param {{ isAnonymous?: boolean }} [context]
 */
export function canComment(role, context = {}) {
  if (context.isAnonymous) return false;
  return getWorkflowRoleConfig(role).canComment;
}

/** @param {string} [role] */
export function canAskQuestion(role) {
  return getWorkflowRoleConfig(role).canAskQuestion;
}

/** @param {string} [role] */
export function canManageCollaborators(role) {
  return getWorkflowRoleConfig(role).canManageCollaborators;
}

/** @param {string} [role] */
export function canAccessAdminWorkspace(role) {
  return getWorkflowRoleConfig(role).canAccessAdminWorkspace;
}

/** @param {string} [role] */
export function usesContributorProjectListCopy(role) {
  return getWorkflowRoleConfig(role).usesContributorProjectListCopy;
}

/** @param {string} [role] */
export function isContributorRole(role) {
  return getWorkflowRoleConfig(role).isContributor;
}

/** @param {string} [role] */
export function isClientRole(role) {
  return getWorkflowRoleConfig(role).isClient;
}

/** @param {string} [role] */
export function isProjectAdminRole(role) {
  return getWorkflowRoleConfig(role).isProjectAdmin;
}

/** @param {string} [role] */
export function isClientPersonaRole(role) {
  return isClientRole(role);
}

/** @param {string} [role] */
export function isProjectAdminPersonaRole(role) {
  return isProjectAdminRole(role);
}

/**
 * Contributor roster role (excludes client / project_admin / empty).
 * @param {string} [role]
 */
export function isContributorPersonaRole(role) {
  const r = normalizeWorkflowRole(role);
  if (!r || isClientRole(r) || isProjectAdminRole(r)) return false;
  return CONTRIBUTOR_WORKFLOW_ROLES.includes(r);
}

/** @param {string[]} roleList */
export function pickPersonaRoleFromList(roleList) {
  const roles = Array.from(
    new Set((Array.isArray(roleList) ? roleList : []).map((value) => normalizeWorkflowRole(value)).filter(Boolean))
  );
  for (const preferred of WORKFLOW_ROLE_PERSONA_PRIORITY) {
    if (roles.includes(preferred)) return preferred;
  }
  const contributor = roles.find((r) => isContributorPersonaRole(r));
  if (contributor) return contributor;
  return "client";
}
